import {
  mdiChevronDown,
  mdiContentCopy,
  mdiDeleteOutline,
  mdiWifi,
} from '@mdi/js'
import Collapse from 'components/Collapse'
import { PropsWithChildren, useCallback, useState } from 'react'
import theme from 'theme_'
import {
  CreateWlanCollapseHeader,
  CreateWlanCollapseHeaderTitle,
  CreateWlanCollapseIcon,
  CreateWlanCollapseBtn,
  CreateWlanLeftBlock,
  CreateWlanRightBlock,
  WLANNameHightlighted,
} from './styles'
import { useIntl } from 'react-intl'
import {
  createSiteWlanThunk,
  deleteWLANThunk,
  deleteSiteSsidPSKsThunk,
} from 'features/networkAi/slice'
import { useAppDispatch } from 'app/hooks'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { networkWLANsMessages } from 'features/networkAi/NetworkDashboard/NetworkWLANs/messages'
import { commonMessages } from 'app/i18n/messages'

interface CreateWlanCollapseProps {
  label: string
  wlan: any
  siteId: string
  defaultExpanded?: boolean
}

function CreateWlanCollapse({
  children,
  label,
  wlan,
  siteId,
  defaultExpanded,
}: PropsWithChildren<CreateWlanCollapseProps>) {
  const [isOpen, setIsOpen] = useState(!!defaultExpanded)
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const [isDeleteWLANModalOpen, setIsDeleteWLANModalOpen] = useState(false)

  const handleConfirmToDeleteWLAN = useCallback(() => {
    setIsDeleteWLANModalOpen(true)
  }, [setIsDeleteWLANModalOpen])

  const handleConfirmDeleteWLAN = useCallback(() => {
    dispatch(deleteWLANThunk(wlan.site_id, wlan.id))
    dispatch(deleteSiteSsidPSKsThunk(wlan.site_id, wlan.ssid))

    setIsDeleteWLANModalOpen(false)
  }, [dispatch, wlan, setIsDeleteWLANModalOpen])

  const handleWLANCopy = async () => {
    let copyWlan = JSON.parse(JSON.stringify(wlan))
    copyWlan.ssid = wlan.ssid + '_Copy'
    await dispatch(createSiteWlanThunk(siteId, copyWlan))
  }

  return (
    <Collapse
      isOpen={isOpen}
      collapseHeader={
        <>
          <ConfirmModal
            isOpen={isDeleteWLANModalOpen}
            title={formatMessage(networkWLANsMessages.titleDelete)}
            confirmBtnTitle={formatMessage(
              networkWLANsMessages.confirmDeleteMessage
            )}
            discardBtnTitle={formatMessage(commonMessages.cancel)}
            variant="primary"
            onConfirm={handleConfirmDeleteWLAN}
            onDiscard={() => setIsDeleteWLANModalOpen(false)}
          >
            {formatMessage(commonMessages.promptConfirm)}
            {formatMessage(networkWLANsMessages.titleDelete)}{' '}
            <WLANNameHightlighted>{wlan.ssid}</WLANNameHightlighted>?
          </ConfirmModal>
          <CreateWlanCollapseHeader>
            <CreateWlanLeftBlock>
              <CreateWlanCollapseIcon
                path={mdiWifi}
                size={1.5}
                color={theme.colors.otterWarmBlack}
              />
              <CreateWlanCollapseHeaderTitle>
                {label}
              </CreateWlanCollapseHeaderTitle>
            </CreateWlanLeftBlock>
            <CreateWlanRightBlock>
              {wlan.id !== undefined && (
                <>
                  <CreateWlanCollapseBtn onClick={handleWLANCopy}>
                    <CreateWlanCollapseIcon
                      path={mdiContentCopy}
                      size={1}
                      color={theme.colors.horizonOrange}
                    />
                  </CreateWlanCollapseBtn>

                  <CreateWlanCollapseBtn onClick={handleConfirmToDeleteWLAN}>
                    <CreateWlanCollapseIcon
                      path={mdiDeleteOutline}
                      size={1}
                      color={theme.colors.horizonOrange}
                    />
                  </CreateWlanCollapseBtn>
                </>
              )}
              <CreateWlanCollapseBtn onClick={handleToggle}>
                <CreateWlanCollapseIcon
                  path={mdiChevronDown}
                  size={1}
                  color={theme.colors.horizonOrange}
                  open={isOpen}
                />
              </CreateWlanCollapseBtn>
            </CreateWlanRightBlock>
          </CreateWlanCollapseHeader>
        </>
      }
    >
      {children}
    </Collapse>
  )
}

export default CreateWlanCollapse
